import React, { memo, useMemo, useState, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { parseISO } from 'date-fns';
import ChevronDownIcon from '@atlaskit/icon/core/migration/chevron-down';
import AkChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import AkCommitIcon from '@atlaskit/icon/core/commit';
import BitbucketCommitsIcon from '@atlaskit/icon/core/migration/commit--bitbucket-commits';

import { token } from '@atlaskit/tokens';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { ISSUE } from '@atlassian/jira-development-board-common/src/common/constants.tsx';
import { DevInfoTypes } from '@atlassian/jira-development-board-common/src/types.tsx';
import DevInfoIcon from '@atlassian/jira-development-board-dev-info-icon/src/main.tsx';
import { useLocaleTimeZone } from '@atlassian/jira-development-common/src/common/utils/hooks/use-locale-timezone/index.tsx';
import RelativeDate from '@atlassian/jira-development-common/src/ui/relative-date/index.tsx';
import { SummaryItem } from '@atlassian/jira-development-summary-common/src/ui/summary-item/index.tsx';
import { FormattedI18nMessage } from '@atlassian/jira-formatted-i18n-message/src/ui/index.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import {
	FireUiAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import type { Commit as CommitType } from '../common/types.tsx';
import { CreateCommitIconButton } from './create-commit-icon-button/index.tsx';
import messages from './messages.tsx';

const COMMIT_SUMMARY_ITEM_TEST_ID = 'development-summary-commit.ui.summary-item';

// eslint-disable-next-line jira/styled/styled-component-order, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.span({
	verticalAlign: 'middle',
});

const CommitIconOld = (
	<IconWrapper aria-hidden>
		<BitbucketCommitsIcon LEGACY_size="medium" spacing="spacious" label="" />
	</IconWrapper>
);

const CommitIcon = (
	<IconWrapper aria-hidden>
		<AkCommitIcon color={token('color.link')} label="" />
	</IconWrapper>
);

type CreateCommitSummaryItemProps = {
	hasCommitCapabilities: boolean;
	shouldForceVisibleSecondary: boolean;
	setShouldForceVisibleSecondary: (arg1: ((arg1: boolean) => boolean) | boolean) => void;
	commitCount?: number;
};

const ConditionalCreateCommitDropdown = importCond<
	typeof import('./create-commit-dropdown/index-new.tsx'),
	typeof import('./create-commit-dropdown/index.tsx')
>(
	'create-commit-dropdown-iv-llc-dev-panel',
	'./create-commit-dropdown/index-new.tsx',
	'./create-commit-dropdown/index.tsx',
);

const CreateCommitSummaryItem = ({
	shouldForceVisibleSecondary,
	setShouldForceVisibleSecondary,
	hasCommitCapabilities,
	commitCount,
}: CreateCommitSummaryItemProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const downChevron = useMemo(
		() =>
			isVisualRefreshEnabled() ? (
				<AkChevronDownIcon
					color={token('color.link')}
					label={formatMessage(messages.createCommitDropdownIconLabel)}
					size="small"
				/>
			) : (
				<ChevronDownIcon
					LEGACY_size="medium"
					spacing="spacious"
					label={formatMessage(messages.createCommitDropdownIconLabel)}
					size="small"
				/>
			),
		[formatMessage],
	);

	const commonSummaryItemProps = {
		title: formatMessage(messages.createCommitTitleLowercase),
		linkFormatting: true,
		icon: isVisualRefreshEnabled() ? CommitIcon : CommitIconOld,
	};

	return hasCommitCapabilities ? (
		<>
			<FireUiAnalytics
				key="viewed button createDevAction"
				action="viewed"
				actionSubject="button"
				actionSubjectId="createDevAction"
				attributes={{
					inline: false,
					summaryItemType: 'commit',
					isCommitSummary: Boolean(commitCount),
				}}
			/>
			<ConditionalCreateCommitDropdown
				onOpenDropdown={({ isOpen }) => {
					setShouldForceVisibleSecondary(isOpen);
				}}
				getDropdownTrigger={({ ref, onClick, ...props }) => (
					<SummaryItem
						secondaryData={downChevron}
						forceVisibleSecondary={shouldForceVisibleSecondary}
						reference={ref}
						{...commonSummaryItemProps}
						{...props}
						onClick={() => {
							onClick();
							fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'createDevAction', {
								inline: false,
								summaryItemType: 'commit',
							});
						}}
						data-testid={COMMIT_SUMMARY_ITEM_TEST_ID}
					/>
				)}
			/>
		</>
	) : null;
};
type Props = {
	hasCommitCapabilities: boolean;
	commit: CommitType | null | undefined;
	onClick?: () => void;
	issueId: string;
	issueKey: string;
};

export const areEqual = (previousProps: Props, nextProps: Props): boolean =>
	nextProps.commit?.count === previousProps.commit?.count &&
	nextProps.commit?.lastUpdated === previousProps.commit?.lastUpdated &&
	nextProps.commit?.url === previousProps.commit?.url;

const CommitInner = memo<Props>(
	({ onClick, commit, hasCommitCapabilities, issueId, issueKey }: Props) => {
		const { formatNumber, formatMessage } = useIntl();
		const { locale, timeZone } = useLocaleTimeZone();
		const [shouldForceVisibleSecondary, setShouldForceVisibleSecondary] = useState(false);

		const { count = 0, lastUpdated } = commit ?? {};
		const title = useMemo(
			() => (
				<FormattedI18nMessage
					componentsMapping={{
						bold: BoldFont,
					}}
					message={formatMessage(messages.title, {
						count: formatNumber(count),
						boldStart: '{boldStart}',
						boldEnd: '{boldEnd}',
					})}
				/>
			),
			[formatMessage, formatNumber, count],
		);
		const relativeDate = useMemo(
			() => (
				<RelativeDateContainer>
					{lastUpdated !== null && lastUpdated !== undefined ? (
						<RelativeDate date={parseISO(lastUpdated)} locale={locale} timeZone={timeZone} strict />
					) : (
						<></>
					)}
				</RelativeDateContainer>
			),
			[lastUpdated, locale, timeZone],
		);

		const summaryItemComponent = (commitInput: CommitType) => (
			<SummaryItem
				title={title}
				relativeDate={relativeDate}
				onClick={onClick}
				itemType="commit"
				itemCount={commitInput.count}
				oneClickUrl={commitInput.url}
				oneClickToolTipContent={formatMessage(messages.tooltip)}
				secondaryHover={
					<ConditionalCreateCommitDropdown
						getDropdownTrigger={(props) => <CreateCommitIconButton {...props} />}
						onOpenDropdown={({ isOpen }) => {
							setShouldForceVisibleSecondary(isOpen);
						}}
					/>
				}
				forceVisibleSecondary={shouldForceVisibleSecondary}
				data-testid={COMMIT_SUMMARY_ITEM_TEST_ID}
			/>
		);

		if (commit && count > 0) {
			return (
				<DevInfoIcon
					devInfoType={DevInfoTypes.COMMIT}
					issueId={Number(issueId)}
					customTrigger={summaryItemComponent(commit)}
					scopeId={`devInfoPanelApp${issueKey}`}
					placement="bottom-end"
					offset={[-8, 8]}
					zIndex={layers.modal}
					location={ISSUE}
				/>
			);
		}

		return (
			<CreateCommitSummaryItem
				hasCommitCapabilities={hasCommitCapabilities}
				shouldForceVisibleSecondary={shouldForceVisibleSecondary}
				setShouldForceVisibleSecondary={setShouldForceVisibleSecondary}
				commitCount={count}
			/>
		);
	},
	areEqual,
);

export const CommitEmptyState = () => {
	const { formatMessage } = useIntl();

	const icon = isVisualRefreshEnabled() ? CommitIcon : CommitIconOld;
	return (
		<SummaryItem
			icon={icon}
			disabled
			title={formatMessage(messages.emptyStateUpdate)}
			data-testid={COMMIT_SUMMARY_ITEM_TEST_ID}
		/>
	);
};

const Commit = memo<Props>((props: Props) => (
	<UFOSegment name="development-summary-commit">
		<CommitInner {...props} />
	</UFOSegment>
));

export default Commit;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BoldFont = styled.span<{ children: ReactNode }>({
	fontWeight: token('font.weight.medium'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RelativeDateContainer = styled.span({
	font: token('font.body.UNSAFE_small'),
	color: token('color.text.subtle'),
});
